/* eslint-disable no-restricted-syntax -- §8/§14.2 wants user-visible Slovak to go through `<T>`, and
   these do: `SidebarSection` renders every item as `<T>{item.label}</T>`, the same way it renders
   legacy's. They are *source strings for the translator*, which is exactly what `NavItem.label` is
   documented as in `generated/nav-tree.ts` - and that file holds sixty-two of them and passes only
   because `src/generated/**` is outside lint. This one is hand-written, so it says so instead. */
import { Ban, Bell, Languages, Layers, LifeBuoy, Receipt, Settings2, Store, Truck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { RouteGuard } from '../module-contract'

/**
 * The sidebar entries for pages **legacy does not have** — `FRONTEND.md` §9.30.
 *
 * `generated/nav-tree.ts` is *"the sidebar exactly as legacy composes it"*, generated from
 * `docs/INVENTORY/manager-nav.json`. Everything §9.5 added to the backend and gave a screen —
 * surcharges, VAT categories, sales channels, the *na sekeru* tabs — is by definition absent from
 * legacy's nav, so the generated file cannot mention it and the pages had **no way in**. The owner
 * found the surcharges one the way anybody would: *"Príplatky a Zálohy kompletne chýbajú"*.
 *
 * They are declared here rather than in the generated file because that file is the parity record and
 * is regenerated; a hand-edit would be overwritten or would misreport what legacy's sidebar contains.
 * Keeping them apart also keeps the two answerable separately — *what did legacy have* and *what did
 * we add* — while being one sidebar on screen.
 *
 * **The guards are the routes' own.** Each one is copied from the module registry entry it points at,
 * so the sidebar cannot offer a page the router then refuses — the §9.7 rule, and the reason
 * `canAccess` takes a `RouteGuard` as readily as a `NavGuard`.
 */
export interface AgosNavItem {
  label: string
  path: string
  icon: LucideIcon
  guard: RouteGuard
}

export interface AgosNavSection {
  heading: string
  items: AgosNavItem[]
  /**
   * Whether the heading is a button that folds the section away.
   *
   * **Absent, this section could never close.** `SidebarSection` reads `section.collapsible !== true`
   * as "always open" and renders the heading as a `<p>` rather than a `<button>`, so the four items
   * below sat open for ever with nothing to click — which is what the owner reported on 2026-08-11:
   * *"Predaj a účtovanie category is not clickable, so these 4 tabs are always visible"*.
   *
   * Six of legacy's nine sections are collapsible and three are not; this one has no legacy
   * counterpart to be faithful to, so it follows the majority rather than inheriting a default
   * nobody chose.
   */
  collapsible: boolean
}

export const AGOS_NAV_SECTIONS: AgosNavSection[] = [
  {
    /**
     * §9.208 — the two hubs, in the section their tabs came from.
     *
     * The heading matches a generated section, so the shell appends these rather than drawing a
     * second block with the same name — and `generate-nav-tree.mjs` drops the seven items they fold
     * up, so *Systémové nastavenia* goes from fifteen entries to ten.
     *
     * **The guards are the routes' own**, which is this file's rule: `anyPermission` for the five
     * settings screens because three of those flags are held by the waiter and one is not, and the
     * translations pair share `translations_admin`.
     */
    heading: 'Systémové nastavenia',
    collapsible: true,
    items: [
      {
        label: 'Nastavenia',
        path: '/settings',
        icon: Settings2,
        guard: {
          systemRoles: ['main_admin', 'admin'],
          anyPermission: [
            'settings_general',
            'settings_sms',
            'settings_email',
            'settings_notifications',
            'settings_monitoring',
          ],
        },
      },
      {
        label: 'Preklady',
        path: '/translations',
        icon: Languages,
        guard: { permission: 'translations_admin' },
      },
      {
        // §9.308, owner item A6. The guard is the three folded routes' own — all three are exactly
        // `{main_admin, admin}`, so unlike *Nastavenia* there is no `anyPermission` to write.
        label: 'Predaj a účtovanie',
        path: '/sales-settings',
        icon: Store,
        guard: { systemRoles: ['main_admin', 'admin'] },
      },
    ],
  },
  {
    /**
     * §9.112 — the owner's point 19. This heading **matches a generated section**, so the shell
     * appends these items to legacy's *Správa rozvoz* rather than drawing a second block with the
     * same name. The driver app used to be reachable only through a top-bar surface switcher, which
     * has gone.
     *
     * §9.759, FB-1078 - **the route's guard, which is what the server asks**: the role Šofér, an
     * administrator, or an account marked *Jazdí aj rozvoz*. §9.112 gated it on a right `driver` for
     * *"ja nastavím účtom čo sú rozvozári, že zobrazovať v právach"* and never added the right, so the
     * roles screen could not offer it and every administrator was refused; the per-account switch
     * that sentence asks for is the mark §9.686 built.
     */
    heading: 'Správa rozvoz',
    collapsible: true,
    items: [
      {
        label: 'Aplikácia pre šoféra',
        path: '/driving',
        icon: Truck,
        guard: { systemRoles: ['driver', 'main_admin', 'admin'], orAlsoDrives: true },
      },
    ],
  },
  {
    /**
     * §9.711, owner O91 - *"in Správa kuchyne new tab Upsell Produktov"*. The heading matches the generated
     * section, so `AppLayout` appends this after *Pizza*, the last item legacy puts there.
     *
     * **The guard is the route's own**, `upsell` - §9.744, BACKEND.md §10A.397, owner O114: *"Práva na upseli
     * chýbajú"*. It was `products` until then, so un-ticking the upsells meant un-ticking the products too.
     */
    heading: 'Správa Kuchyne',
    collapsible: false,
    items: [
      {
        label: 'Upsell produktov',
        path: '/upsell',
        icon: Layers,
        guard: { permission: 'upsell' },
      },
    ],
  },
  {
    /**
     * §9.292 - the owner placed this one: *"účty na sekeru, if its enabled AFTER Mapovanie stolov -
     * in section Správa reštaurácie."*
     *
     * The heading matches a generated section, so `AppLayout` appends this item to it rather than
     * drawing a second block with the same name - and the last item the generator puts there is
     * *Mapovanie stolov*, so "after" holds by construction rather than by a sort key.
     */
    heading: 'Správa Reštaurácie',
    collapsible: false,
    items: [
      {
        label: 'Účty na sekeru',
        path: '/order-tabs',
        icon: Receipt,
        // `OrderTabController` admits a waiter, and this mirrors it - a waiter runs the tabs.
        //
        // §9.281, owner O20: this goes when `na_sekeru` is switched off, and **the slug is the
        // identity** - §9.508, FB-797. §9.503 keyed it on `allowsDeferredPayment` instead, to
        // generalise past the name, and the owner reported the entry showing while the channel was
        // off. On both of their franchises `restaurant` is active and carries that capability, and
        // the two channels are identical on **all five** capability columns - a restaurant table is
        // itself deferred payment. So no capability tells a tab from a table, and a capability guard
        // can only be looser than this one.
        guard: {
          systemRoles: ['main_admin', 'admin', 'waiter'],
          salesChannel: 'na_sekeru',
        },
      },
    ],
  },
  // §9.308, owner item A6 — *"Predaj a účtovanie: also to be moved somewhere sensible"*. The three
  // items that were here are now tabs of `/sales-settings`, and the entry sits in *Systémové
  // nastavenia* above, beside the other two configuration hubs. The whole heading is gone, which is
  // the same simplification A1 measured as fifteen entries becoming ten.
  //
  // **It was banked, and re-reading found the reason had expired.** The note said the section mixed
  // configuration with *Účty na sekeru*, which a waiter runs daily — and §9.281 had already moved
  // that screen into *Správa Reštaurácie*. FB-482 records that a banked question is a statement
  // about the day it was banked.
  {
    // §9.304, FB-474 - the staff inbox had no way in at all: no entry here, none in legacy's
    // inventory, no hub tab, no link, no bell. It was reachable only by typing the path, and four
    // backend paths write to it - low stock, a recurring invoice, an auto-drafted purchase order.
    //
    // *Správa firmy* because that is where `/reports`, `/invoices` and `/reorder-drafts` already
    // live, which are the subjects it announces. That section exists in the generated tree, so this
    // entry is merged into it by heading rather than creating a second one.
    heading: 'Správa firmy',
    collapsible: true,
    items: [
      {
        label: 'Upozornenia',
        path: '/notifications',
        icon: Bell,
        // The route's own guard. The sidebar may not offer what the router refuses - §9.7, FB-001.
        guard: { systemRoles: ['main_admin', 'admin'] },
      },
    ],
  },
  {
    /* §9.787, owner O140 - *"Maybe in Marketing - zablokované"*. Beside *Zákazníci*, whose drawer bans one customer;
     * this is the whole list. The heading matches the generated section, so the entry is merged into it, and its
     * `collapsible` agrees with the generated one's. The guard is the endpoint's own right (§9.7). */
    heading: 'Marketing',
    collapsible: true,
    items: [
      {
        label: 'Zablokované',
        path: '/blocked-contacts',
        icon: Ban,
        guard: { permission: 'marketing_customers_deactivate' },
      },
    ],
  },
  {
    /* §9.513, owner O31 - *"In manager add new tabs at the bottom - something like... NexTable - there make complex
     * ticket system, where they can report bugs etc to main admins from SaaS"*. At the bottom, which is where they
     * asked for it, and its own heading because it is not a setting, a report or a piece of company admin - it is
     * the way out when something is broken.
     *
     * **The guard is the endpoint's five roles**, not an administrator's two: a cook whose printer will not print is
     * the person who knows. §9.7's rule is that the sidebar may not offer what the router refuses, so this list and
     * `SupportTicketController`'s are the same list. */
    heading: 'Podpora',
    collapsible: true,
    items: [
      {
        label: 'Tikety',
        path: '/support',
        icon: LifeBuoy,
        guard: { systemRoles: ['main_admin', 'admin', 'waiter', 'cook', 'driver'] },
      },
    ],
  },
]
